// Added By Farhan Khan Khan on 24-Dec-2025
// Updated: Auto-load all BOM master items on component mount (for new quotations)
// Updated: Improved remove functionality and duplicate prevention
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Table,
  Card,
  Accordion,
} from "react-bootstrap";
import Select from "react-select";
import solisPowerApi from "../../api/solisPowerApi";
import { handleUppercaseChange } from "../common/UpperCaseMethod";
import PubSub from "pubsub-js";
import { PREDEFINED_BOM } from "../../constants/bomPredefinedData";
import "./QuotationMountingStructures.css";
import debounce from "lodash/debounce";

// --- PERFORMANCE OPTIMIZED SUB-COMPONENTS ---
// Created on 2026-03-24 by Farhan Khan - Optimized for large tables
const EditableCell = React.memo(({ value, onChange, placeholder = "", className = "", type = "text", as = "input", rows = 1, style = {} }) => {
  const [localValue, setLocalValue] = useState(value || "");

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  const debouncedOnChange = useRef(
    debounce((nextVal) => onChange(nextVal), 300)
  ).current;

  const handleLocalChange = (e) => {
    const nextVal = e.target.value;
    setLocalValue(nextVal);
    debouncedOnChange(nextVal);
  };

  return (
    <Form.Control
      size="sm"
      as={as}
      rows={as === "textarea" ? rows : undefined}
      type={as === "input" ? type : undefined}
      value={localValue}
      placeholder={placeholder}
      className={className}
      onChange={handleLocalChange}
      style={style}
    />
  );
});

const BomRow = React.memo(({
  item, index, globalIndex,
  specificationOptions, categoryOptions, makeOptions,
  handleItemChange, removeBOMItem, unitOptions,
  handleCategoryChange,
  handleMakeChange
}) => {
  const specsArray = Array.isArray(item.specs) ? item.specs : [];
  const makeArray = Array.isArray(item.make) ? item.make : [];
  const catArray = Array.isArray(item.category) ? item.category : [];

  // ✅ STEP 4: BIND DROPDOWN
  const categoryDropdownValue = item.categoryDropdown || (catArray.map(label => ({
    value: label,
    label: label
  })));

  const makeDropdownValue = item.makeDropdown || (makeArray.map(label => ({
    value: label,
    label: label
  })));

  const specsLabels = useMemo(() => specsArray.map(idx => specificationOptions[idx]?.label || "").filter(Boolean).join(", "), [specsArray, specificationOptions]);
  const catLabels = useMemo(() => catArray.map(val => categoryOptions.find(o => o.value === val)?.label || "").filter(Boolean).join(", "), [catArray, categoryOptions]);
  const makeLabels = useMemo(() => makeArray.map(val => makeOptions.find(o => o.value === val)?.label || "").filter(Boolean).join(", "), [makeArray, makeOptions]);

  return (
    <tr style={{ verticalAlign: "middle" }}>
      <td className="text-center">
        <span className="text-muted small">{item.sr_no || item.sort_order || index + 1}</span>
      </td>
      <td title={item.item_name || ""}>
        <EditableCell
          value={item.item_name || ""}
          className="border-0 bg-transparent fw-bold"
          onChange={(val) => handleItemChange(globalIndex, "item_name", val)}
        />
      </td>
      <td title={specsLabels}>
        <Select
          isMulti
          placeholder="Select Specs..."
          value={specsArray.map(idx => ({
            value: idx,
            label: specificationOptions[idx]?.label || ""
          })).filter(s => s.label)}
          onChange={(selected) => handleItemChange(globalIndex, "specs", selected ? selected.map(s => s.value) : [])}
          options={(specificationOptions || []).map((s, idx) => ({ value: idx, label: s.label }))}
          menuPortalTarget={document.body}
          styles={{
            control: (base) => ({ ...base, minHeight: "28px", fontSize: "0.75rem", border: "none", background: "#f8f9fa" }),
            option: (base) => ({
              ...base,
              whiteSpace: "normal",
              wordBreak: "break-word",
              padding: "8px 12px"
            }),
            singleValue: (base) => ({
              ...base,
              whiteSpace: "normal"
            }),
            multiValue: (base) => ({
              ...base,
              backgroundColor: "#e6f0ff",
              borderRadius: "6px",
              padding: "2px 6px",
              display: "flex",
              alignItems: "center",
              marginRight: "4px"
            }),
            multiValueLabel: (base) => ({
              ...base,
              fontSize: "12px",
              fontWeight: 500,
              color: "#333",
              whiteSpace: "normal"
            }),
            multiValueRemove: (base) => ({
              ...base,
              cursor: "pointer"
            }),
            menu: (base) => ({
              ...base,
              zIndex: 9999,
              minWidth: "200px"
            }),
            menuPortal: base => ({ ...base, zIndex: 9999 }),
          }}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
        />
      </td>
      <td title={catLabels}>
        <Select
          isMulti
          placeholder="Select Category..."
          value={categoryDropdownValue}
          onChange={(val) => handleCategoryChange(val, globalIndex)}
          options={categoryOptions}
          menuPortalTarget={document.body}
          styles={{
            control: (base) => ({ ...base, minHeight: "28px", fontSize: "0.75rem", border: "none", background: "#f8f9fa" }),
            option: (base) => ({
              ...base,
              whiteSpace: "normal",
              wordBreak: "break-word",
              padding: "8px 12px"
            }),
            singleValue: (base) => ({
              ...base,
              whiteSpace: "normal"
            }),
            multiValue: (base) => ({
              ...base,
              backgroundColor: "#e6f0ff",
              borderRadius: "6px",
              padding: "2px 6px",
              display: "flex",
              alignItems: "center",
              marginRight: "4px"
            }),
            multiValueLabel: (base) => ({
              ...base,
              fontSize: "12px",
              fontWeight: 500,
              color: "#333",
              whiteSpace: "normal"
            }),
            multiValueRemove: (base) => ({
              ...base,
              cursor: "pointer"
            }),
            menu: (base) => ({
              ...base,
              zIndex: 9999,
              minWidth: "200px"
            }),
            menuPortal: base => ({ ...base, zIndex: 9999 }),
          }}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
        />
      </td>
      <td>
        <EditableCell
          value={item.size_range_capacity || ""}
          className="border-0 bg-light"
          onChange={(val) => handleItemChange(globalIndex, "size_range_capacity", val)}
        />
      </td>
      <td title={makeLabels}>
        <Select
          isMulti
          placeholder="Select Make..."
          value={makeDropdownValue}
          onChange={(val) => handleMakeChange(val, globalIndex)}
          options={makeOptions}
          menuPortalTarget={document.body}
          styles={{
            control: (base) => ({ ...base, minHeight: "28px", fontSize: "0.75rem", border: "none", background: "#f8f9fa" }),
            option: (base) => ({
              ...base,
              whiteSpace: "normal",
              wordBreak: "break-word",
              padding: "8px 12px"
            }),
            singleValue: (base) => ({
              ...base,
              whiteSpace: "normal"
            }),
            multiValue: (base) => ({
              ...base,
              backgroundColor: "#e6f0ff",
              borderRadius: "6px",
              padding: "2px 6px",
              display: "flex",
              alignItems: "center",
              marginRight: "4px"
            }),
            multiValueLabel: (base) => ({
              ...base,
              fontSize: "12px",
              fontWeight: 500,
              color: "#333",
              whiteSpace: "normal"
            }),
            multiValueRemove: (base) => ({
              ...base,
              cursor: "pointer"
            }),
            menu: (base) => ({
              ...base,
              zIndex: 9999,
              minWidth: "200px"
            }),
            menuPortal: base => ({ ...base, zIndex: 9999 }),
          }}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
        />
      </td>
      <td>
        <Form.Select
          size="sm"
          style={{ fontSize: "0.80rem", border: "none" }}
          value={item.unit || "Nos"}
          onChange={(e) => handleItemChange(globalIndex, "unit", e.target.value)}
        >
          {unitOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </Form.Select>
      </td>
      <td>
        <EditableCell
          type="number"
          value={item.quantity || ""}
          className="border-0 bg-light text-center"
          style={{ width: "60px" }}
          onChange={(val) => handleItemChange(globalIndex, "quantity", val)}
        />
      </td>
      <td title={item.manufacturer_warranty || ""} style={{ minWidth: "150px" }}>
        <EditableCell
          as="textarea"
          rows={3}
          value={item.manufacturer_warranty || ""}
          className="border-0 bg-light"
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: "1.4",
            display: "-webkit-box",
            WebkitLineClamp: "3",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            fontSize: "0.80rem"
          }}
          onChange={(val) => handleItemChange(globalIndex, "manufacturer_warranty", val)}
        />
      </td>
      <td className="text-center">
        <Button variant="link" size="sm" className="text-danger p-0" title="Remove Item" onClick={() => removeBOMItem(globalIndex)}>
          <i className="fa fa-trash"></i>
        </Button>
      </td>
    </tr>
  );
});


const QuotationBomItems = ({
  quotationId,
  quotationBomItems,
  setQuotationBomItems,
  brandOptions,
  // Undo/Redo props
  bomHistory,
  bomFuture,
  saveToBomHistory,
  undoBomChange,
  redoBomChange,
  activeTab
}) => {
  const [bomCategoryOptions, setBomCategoryOptions] = useState([]);
  const [selectedCategoryToImport, setSelectedCategoryToImport] = useState(null);
  const [selectedItemToAdd, setSelectedItemToAdd] = useState(null); // Single item add dropdown
  const [allBomMasterItems, setAllBomMasterItems] = useState([]); // Source: All BOM master items
  const [isInitialized, setIsInitialized] = useState(false); // Track if auto-load has happened
  const [specificationOptions, setSpecificationOptions] = useState([]); // Master Specs
  const initializationRef = useRef(false); // Prevent duplicate initialization

  // New states for fetching actual Category and Brand master values dynamically
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [makeOptions, setMakeOptions] = useState([]);

  useEffect(() => {
    fetchBomCategories();
    fetchSpecifications();
    fetchCategoryMaster();
    fetchMakeMaster();
  }, []);

  // ✅ STEP 3: FIX EDIT CASE (IMPORTANT)
  // Automatically normalize items with dropdown values when labels are found
  useEffect(() => {
    if (quotationBomItems.length > 0) {
      const needsUpdate = quotationBomItems.some(item => 
        (Array.isArray(item.category) && item.category.length > 0 && !item.categoryDropdown) ||
        (Array.isArray(item.make) && item.make.length > 0 && !item.makeDropdown)
      );

      if (needsUpdate) {
        setQuotationBomItems(prev => prev.map(item => {
          const updatedItem = { ...item };
          const catArray = Array.isArray(item.category) ? item.category : [];
          const makeArr = Array.isArray(item.make) ? item.make : [];

          // Map label -> dropdown format
          if (catArray.length > 0 && !item.categoryDropdown) {
            updatedItem.categoryDropdown = catArray.map(label => ({ label, value: label }));
          }

          if (makeArr.length > 0 && !item.makeDropdown) {
            updatedItem.makeDropdown = makeArr.map(label => ({ label, value: label }));
          }

          return updatedItem;
        }));
      }
    }
  }, [quotationBomItems.length]);

  const fetchCategoryMaster = async () => {
    try {
      const categories = await solisPowerApi.fetchCategories();
      if (categories && categories.length > 0) {
        setCategoryOptions(categories.map(c => ({
          label: c.category_name || c.name || "",
          value: c.id
        })));
      }
    } catch (error) {
      console.error("Error fetching category master:", error);
    }
  };

  const fetchMakeMaster = async () => {
    try {
      const brands = await solisPowerApi.fetchActiveBrands();
      if (brands && brands.length > 0) {
        setMakeOptions(brands.map(b => ({
          label: b.name || "",
          value: b.id
        })));
      }
    } catch (error) {
      console.error("Error fetching make master:", error);
    }
  };


  // Fetch all BOM master items for add-dropdown (edit mode: initializeBOM doesn't run, so we need items for dropdown)
  useEffect(() => {
    if (!quotationId) return; // Create mode: initializeBOM will populate allBomMasterItems
    async function loadMasterItems() {
      const items = await solisPowerApi.fetchBomMasterItems();
      if (items && items.length > 0) {
        setAllBomMasterItems(items.filter(i => i.status === 'Active'));
      }
    }
    loadMasterItems();
  }, [quotationId]);

  // Separate effect for auto-loading BOM items (only for new quotations)
  // Updated on 2026-03-24 by Farhan Khan - Load on tab activation
  useEffect(() => {
    if (activeTab === "bom" && !quotationId && (!quotationBomItems || quotationBomItems.length === 0)) {
      initializeBOM();
    } else if (quotationId || (quotationBomItems && quotationBomItems.length > 0)) {
      setIsInitialized(true);
      initializationRef.current = true;
    }
  }, [quotationId, quotationBomItems.length, activeTab]);

  const fetchBomCategories = async () => {
    const categories = await solisPowerApi.fetchActiveBomCategories();
    if (categories) {
      const options = categories.map(cat => ({
        value: cat.id,
        label: cat.name,
        bom_type: cat.bom_type || 'STANDARD',
      }));
      setBomCategoryOptions(options);
    }
  };


  const fetchSpecifications = async () => {
    const specs = await solisPowerApi.fetchSpecifications();
    if (specs) {
      const options = specs.map(s => ({
        id: s.id,
        name: s.name,
        label: s.name,
        value: s.id
      }));
      setSpecificationOptions(options);
    }
  };


  // Build dropdown options: all STANDARD BOM line items (for add single item - removed items stay available)
  const bomItemOptions = React.useMemo(() => {
    if (!allBomMasterItems.length) return [];
    const categories = bomCategoryOptions.reduce((acc, c) => {
      acc[c.value] = { name: c.label, bom_type: c.bom_type };
      return acc;
    }, {});
    return allBomMasterItems
      .filter(item => {
        const cat = categories[item.bom_categories_id];
        return cat && cat.bom_type === 'STANDARD' && item.status === 'Active';
      })
      .sort((a, b) => (a.s_no ?? 9999) - (b.s_no ?? 9999))
      .map(item => ({
        value: item.id,
        label: `${item.name || 'Item'} (${categories[item.bom_categories_id]?.name || 'Uncategorized'})`,
        masterItem: item,
        categoryName: categories[item.bom_categories_id]?.name || 'Uncategorized',
      }));
  }, [allBomMasterItems, bomCategoryOptions]);

  /**
   * Initialize BOM: Auto-load all active BOM master items for new quotations
   * Added By Farhan Khan Khan on 24-Dec-2025
   * Purpose: Load all BOM master items by default when creating a new quotation
   */
  /**
   * Initialize BOM from Excel structure (Predefined Data)
   * Create Mode: Auto-loads predefined rows with intelligent mapping
   */
  const initializeBOM = async () => {
    if (initializationRef.current) return;
    initializationRef.current = true;

    try {
      // Fetch dynamic dependencies for matching logic
      const categories = await solisPowerApi.fetchActiveBomCategories();
      const catOptions = categories ? categories.map(cat => ({ id: cat.id, label: cat.name })) : [];

      const specs = await solisPowerApi.fetchSpecifications();
      const specOptions = specs ? specs.map(s => ({ id: s.id, label: s.name })) : [];

      // Wait for master dropdown values if not loaded
      let currentMakeOptions = makeOptions;
      if (currentMakeOptions.length === 0) {
        try {
          const res = await solisPowerApi.fetchActiveBrands();
          if (res) currentMakeOptions = res.map(b => ({ label: b.name, value: b.id }));
          setMakeOptions(currentMakeOptions);
        } catch (e) { }
      }

      let currentCategoryOptions = categoryOptions;
      if (currentCategoryOptions.length === 0) {
        try {
          const res = await solisPowerApi.fetchCategories();
          if (res) currentCategoryOptions = res.map(c => ({ label: c.category_name || c.name, value: c.id }));
          setCategoryOptions(currentCategoryOptions);
        } catch (e) { }
      }

      const brands = currentMakeOptions;

      // Helper for auto-matching labels to indices
      const findIndices = (predefinedLabels, systemOptions) => {
        if (!predefinedLabels || !Array.isArray(predefinedLabels)) return [];
        return predefinedLabels
          .map(label => systemOptions.findIndex(opt => opt.label.toLowerCase() === label.toLowerCase()))
          .filter(idx => idx !== -1);
      };

      const findIndex = (predefinedLabel, systemOptions) => {
        if (!predefinedLabel) return [];
        const idx = systemOptions.findIndex(opt => opt.label.toLowerCase() === predefinedLabel.toLowerCase());
        return idx !== -1 ? [idx] : [];
      };

      // ✅ Helper for matching and pushing the LABEL (Name) instead of ID
      const findLabels = (predefinedLabels, systemOptions) => {
        if (!predefinedLabels || !Array.isArray(predefinedLabels)) return [];
        return predefinedLabels.map(label => {
          const matched = systemOptions.find(opt => opt.label.toLowerCase() === label.toLowerCase());
          return matched ? (matched.label || matched.name || label) : label;
        }).filter(val => val !== null);
      };

      let bomItemsToAdd = [];
      let globalIndex = 0;

      Object.entries(PREDEFINED_BOM).forEach(([sectionKey, sectionData]) => {
        // Format section name (e.g., 'solar_panels' -> 'SOLAR PANELS')
        const sectionName = sectionKey.replace(/_/g, ' ').toUpperCase();

        const processItem = (item) => {
          // Normalize inputs to arrays
          const itemSpecs = Array.isArray(item.specifications) ? item.specifications : (item.specification ? [item.specification] : []);
          const itemCats = Array.isArray(item.category) ? item.category : (item.category ? [item.category] : []);
          const itemMakes = Array.isArray(item.make) ? item.make : (item.make ? [item.make] : []);

          bomItemsToAdd.push({
            id: `excel_${Date.now()}_${globalIndex}`,
            section: sectionName,
            item_name: item.product_name || "",
            description: item.details || "",
            specification: itemSpecs.join(', ') || "",

            // ✅ Auto-matching Logic (Store labels only)
            category: findLabels(itemCats, currentCategoryOptions),
            make: findLabels(itemMakes, currentMakeOptions),
            specs: findIndices(itemSpecs, specOptions),

            size_range_capacity: item.capacity || item.size || "",
            quantity: item.quantity || 1,
            unit: item.unit || "Nos",
            manufacturer_warranty: item.manufacturer_warranty || "",

            display_category: sectionName,
            sort_order: globalIndex + 1,
            sr_no: globalIndex + 1, // Store sequence for correct sorting later
            sequence: globalIndex, // Explicitly set sequence
            status: "Active"
          });
          globalIndex++;
        };

        if (Array.isArray(sectionData)) {
          sectionData.forEach(processItem);
        } else if (typeof sectionData === 'object' && sectionData !== null) {
          Object.values(sectionData).forEach(processItem);
        }
      });


      if (bomItemsToAdd.length > 0) {
        setQuotationBomItems(bomItemsToAdd);
      }
      setIsInitialized(true);
    } catch (error) {
      console.error("Error initializing BOM from Excel:", error);
      setIsInitialized(true); // Mark as initialized even on error to prevent retries
    }
  };

  /**
   * Add BOM Item: Add a single BOM master item to the quotation list
   * Added By Farhan Khan Khan on 24-Dec-2025
   * Purpose: Add items via dropdown import (for re-adding removed items)
   */
  const addBOMItem = (masterItem, categoryName) => {
    saveToBomHistory();
    // Check for duplicates by bom_master_item_id
    const existingItem = quotationBomItems.find(
      item => item.bom_master_item_id === masterItem.id
    );

    if (existingItem) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Duplicate Item",
        message: `"${masterItem.name}" is already in the BOM list`,
      });
      return false;
    }

    // Get next s_no / sort_order (ORDER BY s_no ASC)
    const maxSno = quotationBomItems.length > 0
      ? Math.max(...quotationBomItems.map(item => (item.s_no ?? item.sort_order ?? 0)))
      : 0;
    const nextSno = maxSno + 1;

    // EXACT same fields as BOM line item form - 1:1 mapping
    const newItem = {
      id: `temp_${Date.now()}`,
      bom_master_item_id: masterItem.id,
      s_no: nextSno,
      product_id: masterItem.product_id ?? null,
      product_name: masterItem.product_name ?? masterItem.name ?? "",
      specification: masterItem.specification ?? "",
      category: (() => { 
        const v = masterItem.category; 
        const arr = !v ? [] : Array.isArray(v) ? v : (typeof v === 'string' ? (v.startsWith('[') ? JSON.parse(v) : [v]) : [v]);
        // Map any UUIDs to labels if found in master options
        return arr.map(val => {
          const opt = categoryOptions.find(o => o.value === val);
          return opt ? opt.label : val;
        });
      })(),
      size_range_capacity: masterItem.size_range_capacity ?? "",
      make: (() => { 
        const v = masterItem.make; 
        const arr = !v ? [] : Array.isArray(v) ? v : (typeof v === 'string' ? (v.startsWith('[') ? JSON.parse(v) : [v]) : [v]);
        // Map any UUIDs to labels if found in master options
        return arr.map(val => {
          const opt = makeOptions.find(o => o.value === val);
          return opt ? opt.label : val;
        });
      })(),
      unit: masterItem.unit || "Nos",
      quantity: masterItem.quantity ?? "",
      manufacturer_warranty: masterItem.manufacturer_warranty ?? "",
      name: masterItem.name || "",
      item_name: masterItem.name || "",
      description: masterItem.description || "",
      brand_id: (() => { const v = masterItem.brand_id; return !v ? [] : Array.isArray(v) ? v : [v]; })(),
      brand_name: masterItem.brand_name || "",
      status: masterItem.status || "Active",
      display_category: categoryName || "Uncategorized",
      sort_order: nextSno,
    };

    setQuotationBomItems([...quotationBomItems, newItem]);
    return true;
  };

  /**
   * Remove BOM Item: Remove an item from the quotation BOM list
   * Added By Farhan Khan Khan on 24-Dec-2025
   * Purpose: Remove items from quotation (does not affect BOM master)
   */
  const removeBOMItem = (indexToRemove) => {
    saveToBomHistory();
    const itemToRemove = quotationBomItems[indexToRemove];
    const newItems = quotationBomItems.filter((_, index) => index !== indexToRemove);

    // Re-sequence s_no and sort_order (ORDER BY s_no ASC)
    const updatedItems = newItems.map((item, index) => ({
      ...item,
      s_no: index + 1,
      sort_order: index + 1,
    }));

    setQuotationBomItems(updatedItems);

    PubSub.publish("RECORD_SAVED_TOAST", {
      title: "Item Removed",
      message: `"${itemToRemove.item_name || 'Item'}" removed from BOM list`,
    });
  };

  /**
   * Remove BOM Section: Remove all items in a specific section/category
   * Added By Farhan Khan Khan on 24-Dec-2025
   * Purpose: Bulk remove all items in a section with single click
   */
  const removeBOMSection = (categoryName) => {
    saveToBomHistory();
    const itemsInSection = quotationBomItems.filter(
      item => (item.section || item.display_category) === categoryName
    );

    if (itemsInSection.length === 0) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Info",
        message: `No items found in "${categoryName}" section`,
      });
      return;
    }

    // Remove all items from this section
    const remainingItems = quotationBomItems.filter(
      item => (item.section || item.display_category) !== categoryName
    );

    // Recalculate sort_order
    const updatedItems = remainingItems.map((item, index) => ({
      ...item,
      sort_order: index + 1,
    }));

    setQuotationBomItems(updatedItems);

    PubSub.publish("RECORD_SAVED_TOAST", {
      title: "Section Removed",
      message: `${itemsInSection.length} item(s) removed from "${categoryName}" section`,
    });
  };


  /**
   * Handle Import From Category: Import BOM items from a selected category
   * Updated By Farhan Khan on 24-Dec-2025
   * Purpose: Allow users to add back removed items or add additional items manually
   * Prevents duplicates by checking bom_master_item_id
   */
  const handleImportFromCategory = async () => {
    saveToBomHistory();
    if (!selectedCategoryToImport) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "Please select a BOM Category to import",
      });
      return;
    }

    try {
      // Fetch category data to get bom_type
      // Fixed by Farhan Khan on 31-Jan-2026 - Support Mounting Structure import
      const categoryData = await solisPowerApi.fetchBomCategoryWithItems(selectedCategoryToImport.value);
      const categoryBomType = categoryData?.bom_type || 'STANDARD';

      // Fetch BOM master items for selected category
      const masterItems = await solisPowerApi.fetchBomMasterItemsByCategory(selectedCategoryToImport.value);

      if (masterItems && masterItems.length > 0) {
        const category = bomCategoryOptions.find(c => c.value === selectedCategoryToImport.value);
        const categoryName = category ? category.label : "";

        // Filter out duplicates and add only new items
        const existingMasterItemIds = new Set(
          quotationBomItems
            .map(item => item.bom_master_item_id)
            .filter(id => id !== null && id !== undefined)
        );

        const newItems = [];
        let addedCount = 0;
        let skippedCount = 0;

        // Helper to parse JSON arrays
        const parseJsonArray = (field) => {
          if (!field) return [];
          if (Array.isArray(field)) return field;
          if (typeof field === 'string') {
            try {
              const parsed = JSON.parse(field);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          }
          return [];
        };

        masterItems.forEach((masterItem) => {
          if (!existingMasterItemIds.has(masterItem.id)) {
            const nextSno = quotationBomItems.length + newItems.length + 1;

            // Detect if this is a Mounting Structure item
            const isMountingStructure = categoryBomType === 'MOUNTING_STRUCTURE' ||
              masterItem.item_type === 'COMPONENT' ||
              masterItem.item_type === 'COMBO_DISTRIBUTION' ||
              masterItem.item_type === 'SPECIAL_STRUCTURE';

            // Base item - EXACT fields from BOM line item form
            const baseItem = {
              id: `temp_${Date.now()}_${newItems.length}`,
              bom_master_item_id: masterItem.id,
              s_no: masterItem.s_no ?? nextSno,
              product_id: masterItem.product_id ?? null,
              product_name: masterItem.product_name ?? masterItem.name ?? "",
              specification: masterItem.specification ?? "",
              category: (() => { 
                const v = masterItem.category; 
                const arr = !v ? [] : Array.isArray(v) ? v : (typeof v === 'string' ? (v.startsWith('[') ? JSON.parse(v) : [v]) : [v]);
                return arr.map(val => {
                  const opt = categoryOptions.find(o => o.value === val);
                  return opt ? opt.label : val;
                });
              })(),
              size_range_capacity: masterItem.size_range_capacity ?? "",
              make: (() => { 
                const v = masterItem.make; 
                const arr = !v ? [] : Array.isArray(v) ? v : (typeof v === 'string' ? (v.startsWith('[') ? JSON.parse(v) : [v]) : [v]);
                return arr.map(val => {
                  const opt = makeOptions.find(o => o.value === val);
                  return opt ? opt.label : val;
                });
              })(),
              unit: masterItem.unit || "Nos",
              quantity: masterItem.quantity ?? "",
              manufacturer_warranty: masterItem.manufacturer_warranty ?? "",
              name: masterItem.name || "",
              item_name: masterItem.name || "",
              description: masterItem.description || "",
              brand_id: (() => { const v = masterItem.brand_id; return !v ? [] : Array.isArray(v) ? v : [v]; })(),
              brand_name: masterItem.brand_name || "",
              display_category: categoryName,
              section: categoryName, // Use category as section for grouping
              sort_order: masterItem.s_no ?? nextSno,
            };


            // Add Mounting Structure fields if applicable (EXACT MountingStructureForm structure)
            if (isMountingStructure) {
              // Parse parts from JSONB if exists
              let parts = [];
              if (masterItem.parts) {
                try {
                  parts = typeof masterItem.parts === 'string' ? JSON.parse(masterItem.parts) : masterItem.parts;
                  if (!Array.isArray(parts)) parts = [];
                } catch {
                  parts = [];
                }
              }

              newItems.push({
                ...baseItem,
                item_type: masterItem.item_type || 'COMPONENT',
                component_name: masterItem.component_name || "",
                specs: parseJsonArray(masterItem.specs),
                size: masterItem.size || "",
                shape: parseJsonArray(masterItem.shape),
                make: parseJsonArray(masterItem.make),
                others: masterItem.others || "",
                combo_roof_type: masterItem.combo_roof_type || null,
                number_of_panels: masterItem.number_of_panels || null,
                special_structure_type: masterItem.special_structure_type || null,
                structure_height_front: masterItem.structure_height_front || null,
                structure_height_back: masterItem.structure_height_back || null,
                tin_shed_structure_type: masterItem.tin_shed_structure_type || null,
                tin_shed_mounting_type: masterItem.tin_shed_mounting_type || null,
                length: masterItem.length || null,
                notes: masterItem.notes || null,
                parts: parts, // JSONB parts array
              });
            } else {
              newItems.push(baseItem);
            }

            addedCount++;
          } else {
            skippedCount++;
          }
        });

        if (newItems.length > 0) {
          setQuotationBomItems([...quotationBomItems, ...newItems]);

          PubSub.publish("RECORD_SAVED_TOAST", {
            title: "Success",
            message: `${addedCount} item(s) imported from ${categoryName}${skippedCount > 0 ? ` (${skippedCount} duplicate(s) skipped)` : ''}`,
          });
        } else {
          PubSub.publish("RECORD_ERROR_TOAST", {
            title: "Info",
            message: `All items from "${categoryName}" are already in the BOM list`,
          });
        }

        setSelectedCategoryToImport(null);
      } else {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Info",
          message: "No active items found in selected category",
        });
      }
    } catch (error) {
      console.error("Error importing BOM items:", error);
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "Failed to import BOM items",
      });
    }
  };

  const addManualRow = () => {
    saveToBomHistory();
    // EXACT same fields as BOM line item form (BomEdit addItemRow)
    const nextSno = quotationBomItems.length + 1;
    const newItem = {
      id: `temp_${Date.now()}`,
      section: "Custom/Manual Items",
      bom_master_item_id: null,
      s_no: nextSno,
      sr_no: nextSno,
      product_id: null,
      product_name: "",
      item_name: "Custom/Manual Items",
      specification: "",
      category: [],
      specs: [],
      make: [],
      unit: "Nos",
      quantity: 1,
      manufacturer_warranty: "",
      status: "Active",
      display_category: "Custom/Manual Items",
      section: "Custom/Manual Items",
      sort_order: nextSno,
    };

    setQuotationBomItems([...quotationBomItems, newItem]);
  };

  const addSectionRow = (e, targetSection) => {
    e.stopPropagation(); // Prevent accordion from toggling when clicking the button
    saveToBomHistory();

    // Find max sequence across all items
    const maxSequence = quotationBomItems.length > 0
      ? Math.max(...quotationBomItems.map(item => item.sequence || 0))
      : 0;
    const nextSequence = maxSequence + 1;

    const nextSno = quotationBomItems.length + 1;

    const newItem = {
      id: `temp_${Date.now()}`,
      section: targetSection,
      bom_master_item_id: null,
      s_no: nextSno,
      sr_no: nextSno,
      product_id: null,
      product_name: targetSection || "",
      item_name: targetSection || "",
      specification: "",
      category: [],
      specs: [],
      make: [],
      unit: "Nos",
      quantity: null,
      manufacturer_warranty: "",
      status: "Active",
      display_category: targetSection,
      sequence: nextSequence,
      sort_order: nextSno,
    };

    setQuotationBomItems(prev => {
      const items = [...prev];
      // Find the index of the last item in this section based on CURRENT state
      let lastIndexInSection = -1;
      for (let i = items.length - 1; i >= 0; i--) {
        const itemSection = items[i].section || items[i].display_category || "Uncategorized";
        if (itemSection === targetSection) {
          lastIndexInSection = i;
          break;
        }
      }

      const newItems = [...items];
      if (lastIndexInSection !== -1) {
        newItems.splice(lastIndexInSection + 1, 0, newItem);
      } else {
        newItems.push(newItem);
      }
      return newItems;
    });
  };

  // Deprecated: Use removeBOMItem instead
  // Kept for backward compatibility
  const removeRow = (indexToRemove) => {
    removeBOMItem(indexToRemove);
  };

  const handleCategoryChange = (selectedOptions, index) => {
    saveToBomHistory();
    setQuotationBomItems(prev => {
      const updated = [...prev];
      if (!updated[index]) return prev;

      // ✅ STEP 1: STORE LABELS INSTEAD OF UUID
      updated[index].category = selectedOptions
        ? selectedOptions.map(opt => opt.label)
        : [];

      // optional for UI representation
      updated[index].display_category = updated[index].category.join(", ");

      // convert UUID → dropdown format for binding
      updated[index].categoryDropdown = selectedOptions || [];

      console.log('[BOM] handleCategoryChange (Labels):', updated[index].category);
      return updated;
    });
  };

  const handleMakeChange = (selectedOptions, index) => {
    saveToBomHistory();
    setQuotationBomItems(prev => {
      const updated = [...prev];
      if (!updated[index]) return prev;

      // ✅ STEP 1: STORE LABELS
      updated[index].make = selectedOptions
        ? selectedOptions.map(opt => opt.label)
        : [];

      updated[index].makeDropdown = selectedOptions || [];

      console.log('[BOM] handleMakeChange (Labels):', updated[index].make);
      return updated;
    });
  };

  const handleItemChange = (index, field, value) => {
    if (field === 'category') {
      const options = Array.isArray(value) ? value : [];
      handleCategoryChange(options, index);
      return;
    }
    if (field === 'make') {
      const options = Array.isArray(value) ? value : [];
      handleMakeChange(options, index);
      return;
    }
    saveToBomHistory();
    setQuotationBomItems(prev => {
      const newItems = [...prev];
      if (!newItems[index]) return prev;

      let processedVal = value;
      // Handle array results from Select components
      if (['specs'].includes(field)) {
        if (Array.isArray(value)) {
          processedVal = value.map(v => (typeof v === 'object' && v !== null && v.value !== undefined) ? v.value : v);
        } else {
          processedVal = [];
        }
      }

      const updatedItem = { ...newItems[index], [field]: processedVal };
      newItems[index] = updatedItem;
      console.log('[BOM] handleItemChange:', field, processedVal);
      return newItems;
    });
  };

  const handleReorder = (index, direction) => {
    saveToBomHistory();
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === quotationBomItems.length - 1)
    ) {
      return;
    }

    const newItems = [...quotationBomItems];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];

    // Update sort_order and sr_no for display sequence
    newItems.forEach((item, idx) => {
      item.sort_order = idx + 1;
      item.sr_no = idx + 1;
    });

    setQuotationBomItems(newItems);
  };

  // Group items by section (Excel Structure) or display_category (Fallback)
  const filteredBomItems = quotationBomItems.filter(item => {
    const isMountingStructure = item.item_type === 'COMPONENT' ||
      item.item_type === 'COMBO_DISTRIBUTION' ||
      item.item_type === 'SPECIAL_STRUCTURE' ||
      (item.display_category && item.display_category.toLowerCase().includes('mounting'));
    return !isMountingStructure;
  });

  const groupedItems = [...filteredBomItems]
    .sort((a, b) => (a.sr_no ?? a.s_no ?? 9999) - (b.sr_no ?? b.s_no ?? 9999))
    .reduce((acc, item) => {
      const section = item.section || item.display_category || "Uncategorized";
      if (!acc[section]) {
        acc[section] = [];
      }
      acc[section].push(item);
      return acc;
    }, {});

  const unitOptions = [
    { value: "Nos", label: "Nos" },
    { value: "Set", label: "Set" },
    { value: "Mtr", label: "Mtr" },
    { value: "SqM", label: "SqM" },
    { value: "Kg", label: "Kg" },
    { value: "KWp", label: "KWp" },
    { value: "KWh", label: "KWh" },
    { value: "Box", label: "Box" },
    { value: "Lot", label: "Lot" },
  ];

  return (
    <div className="mt-4" style={{ width: "100%", maxWidth: "100%" }}>
      {/* BOM Header with Import and Add Row */}
      <Row className="mb-3 mx-0">
        <Col lg={6}>
          <h5 className="fw-bold" style={{ color: "#364794" }}>Bill of Materials</h5>
          <p className="text-muted small">
            {quotationId
              ? "Edit BOM items for this quotation"
              : "Predefined BOM items loaded from Excel structure. Auto-matching dynamic fields."}
          </p>
        </Col>
        <Col lg={6} className="text-end d-flex gap-2 justify-content-end align-items-center">
          <div className="d-flex gap-1 me-2 border-end pe-2">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={undoBomChange}
              disabled={!bomHistory || bomHistory.length === 0}
              title="Undo (Ctrl+Z)"
            >
              <i className="fa fa-undo"></i>
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={redoBomChange}
              disabled={!bomFuture || bomFuture.length === 0}
              title="Redo (Ctrl+Y)"
            >
              <i className="fa fa-redo"></i>
            </Button>
          </div>
          {/* <div style={{ width: "200px" }}>
            <Select
              placeholder="Add item..."
              onChange={(opt) => {
                if (opt && opt.masterItem) {
                  addBOMItem(opt.masterItem, opt.categoryName);
                  setSelectedItemToAdd(null);
                }
              }}
              options={bomItemOptions}
              value={selectedItemToAdd}
              isClearable
              menuPortalTarget={document.body}
              styles={{
                control: (base) => ({ ...base, minHeight: "31px", fontSize: "0.85rem" }),
                menuPortal: base => ({ ...base, zIndex: 9999 })
              }}
            />
          </div> */}
          <Button size="sm" className="btn-create" onClick={addManualRow}>
            <i className="fa fa-plus"></i> Add Section
          </Button>
        </Col>
      </Row>

      {filteredBomItems.length === 0 && isInitialized ? (
        <Card className="text-center py-5 mx-0">
          <Card.Body>
            <i className="fa fa-box fa-3x text-muted mb-3"></i>
            <h6 className="text-muted">No BOM items</h6>
            <Button
              size="sm"
              className="btn-create"
              onClick={() => {
                initializationRef.current = false;
                setIsInitialized(false);
                initializeBOM();
              }}
            >
              <i className="fa fa-refresh"></i> Reset to Template
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Accordion alwaysOpen defaultActiveKey={Object.keys(groupedItems).map((_, i) => i.toString())} className="bom-accordion">
          {Object.entries(groupedItems).map(([section, items], sectionIndex) => (
            <Accordion.Item key={section} eventKey={sectionIndex.toString()}>

              <Accordion.Header>
                <div className="d-flex align-items-center justify-content-between w-100 pe-3">
                  <div>
                    {/* <span className="badge bg-custom me-2">{sectionIndex + 1}</span> */}
                    <span className="fw-bold" style={{ color: "#364794" }}>{section}</span>
                    <span className="text-muted ms-2 small">({items.length} items)</span>
                  </div>
                  <div>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={(e) => addSectionRow(e, section)}
                      className="py-0 px-2"
                      style={{ fontSize: "0.80rem" }}
                    >
                      <i className="fa fa-plus me-1"></i> Add Item Row
                    </Button>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body className="p-0">
                <div className="table-responsive">
                  <Table striped bordered hover size="sm" className="mb-0" style={{ width: "100%", tableLayout: "auto", fontSize: "0.85rem" }}>
                    <thead className="bg-light">
                      <tr className="text-uppercase" style={{ backgroundColor: "#f8f9fa" }}>
                        <th style={{ width: "50px" }} className="text-center">SN</th>

                        <th style={{ width: "180px" }}>ITEM NAME</th>

                        <th style={{ width: "300px" }}>SPECS (MULTI)</th>

                        <th style={{ width: "240px" }}>CATEGORY</th>

                        <th style={{ width: "120px" }}>SIZE/RANGE</th>

                        <th style={{ width: "250px" }}>MAKE</th>

                        <th style={{ width: "90px" }}>UNIT</th>

                        <th style={{ width: "90px" }}>QTY</th>

                        <th style={{ width: "150px" }}>WARRANTY</th>

                        <th style={{ width: "30px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, itemIndex) => {
                        const globalIndex = quotationBomItems.findIndex(i => i === item);
                        return (
                          <BomRow
                            key={item.id || globalIndex}
                            item={item}
                            index={itemIndex}
                            globalIndex={globalIndex}
                            specificationOptions={specificationOptions}
                            categoryOptions={categoryOptions}
                            makeOptions={makeOptions}
                            handleItemChange={handleItemChange}
                            handleCategoryChange={handleCategoryChange}
                            handleMakeChange={handleMakeChange}
                            removeBOMItem={removeBOMItem}
                            unitOptions={unitOptions}
                          />
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </div>
  );
};

export default QuotationBomItems;

